import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, X, MapPin, Sparkles, ShieldCheck, SlidersHorizontal, Undo2, Users2, BadgeCheck, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { formatDistanceKm } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { CLUBS } from "@/data/clubs";
import { labelForConnection } from "@/data/connections";
import { FAN_TYPES, tagForFanType } from "@/data/fanTypes";
import BottomNav from "@/components/app/BottomNav";
import SafetyMenu from "@/components/app/SafetyMenu";
import { toast } from "sonner";

const clubColor = (name) => CLUBS.find(c => c.name === name)?.color || "#152A55";
const photoSrc = (url) => (url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

function MatchCelebration({ open, name, onClose, onUndo, undoing }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[60] flex items-center justify-center bg-footy-bg/95 backdrop-blur-lg p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="match-modal">
          <motion.div className="text-center" initial={{ scale: 0.7 }} animate={{ scale: 1 }}>
            <p className="text-footy-red text-sm uppercase tracking-[0.3em] font-bold">Full time whistle</p>
            <h2 className="font-display text-5xl sm:text-6xl mt-3 uppercase text-white leading-none">You&apos;ve got a<br/><span className="text-footy-red">Footymatch!</span></h2>
            <p className="text-slate-300 mt-4 text-lg">You and <span className="text-footy-red font-bold">{name}</span> are ready to kick off.</p>
            <button onClick={onClose} className="footy-btn-primary mt-8" data-testid="match-close">Keep swiping</button>
            {onUndo && (
              <div className="mt-4">
                <button onClick={onUndo} disabled={undoing} data-testid="match-undo"
                  className="text-xs uppercase tracking-widest text-slate-400 hover:text-yellow-300 disabled:opacity-40 inline-flex items-center gap-1">
                  <Undo2 size={12} /> {undoing ? "Undoing…" : "Undo — I swiped by accident"}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Discover() {
  const { user } = useAuth();
  const myTypes = user?.profile?.connection_types || [];
  const myClub = user?.profile?.club || "";
  const hasPostcode = !!user?.profile?.postcode;
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [match, setMatch] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]); // empty == all my types
  const [activeFanTypes, setActiveFanTypes] = useState([]); // fan_type filter chips
  const [maxKm, setMaxKm] = useState(user?.profile?.max_distance_km || 50);
  const [minAge, setMinAge] = useState(user?.profile?.min_age || 18);
  const [maxAge, setMaxAge] = useState(user?.profile?.max_age || 99);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [lastSwiped, setLastSwiped] = useState(null); // { profile, action }
  const [undoing, setUndoing] = useState(false);
  // Fellow-fans-first toggle. Default ON — Footymatch's north star is same-club matching.
  // Persisted in localStorage so the user's choice sticks across sessions.
  const [sameClubFirst, setSameClubFirst] = useState(() => {
    try { const v = localStorage.getItem("fm_same_club_first"); return v === null ? true : v === "1"; }
    catch { return true; }
  });
  const setSameClub = (v) => {
    setSameClubFirst(v);
    try { localStorage.setItem("fm_same_club_first", v ? "1" : "0"); } catch { /* ignore */ }
  };
  // First-time coach mark for the filter icon. Silently self-dismisses after any interaction.
  const [showFilterTip, setShowFilterTip] = useState(() => {
    try { return !localStorage.getItem("fm_seen_filter_tip"); } catch { return false; }
  });
  const dismissFilterTip = () => {
    setShowFilterTip(false);
    try { localStorage.setItem("fm_seen_filter_tip", "1"); } catch { /* ignore */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeFilters.length) params.types = activeFilters.join(",");
      if (activeFanTypes.length) params.fan_types = activeFanTypes.join(",");
      if (hasPostcode) params.max_km = maxKm;
      params.same_club_first = sameClubFirst ? "true" : "false";
      const { data } = await api.get("/discover", { params });
      setProfiles(data);
    } catch { toast.error("Could not load profiles"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeFilters.join(","), activeFanTypes.join(","), maxKm, minAge, maxAge, sameClubFirst]);

  // Debounced persist of preferences to the server so slider changes stick across sessions.
  // Uses a single pending-patch object so rapid changes to DIFFERENT sliders don't lose data —
  // each call merges into the pending payload; only the flush is debounced.
  const savePrefTimer = useRef(null);
  const pendingPrefs = useRef({});
  const savePrefs = (patch) => {
    pendingPrefs.current = { ...pendingPrefs.current, ...patch };
    if (savePrefTimer.current) clearTimeout(savePrefTimer.current);
    savePrefTimer.current = setTimeout(async () => {
      const payload = pendingPrefs.current;
      pendingPrefs.current = {};
      if (!Object.keys(payload).length) return;
      try { await api.patch("/profile/preferences", payload); }
      catch { toast.error("Could not save preferences"); }
    }, 700);
  };
  const onMaxKm = (v) => { setMaxKm(v); savePrefs({ max_distance_km: v }); };
  const onMinAge = (v) => {
    const capped = Math.min(v, maxAge);
    setMinAge(capped); savePrefs({ min_age: capped });
  };
  const onMaxAge = (v) => {
    const floored = Math.max(v, minAge);
    setMaxAge(floored); savePrefs({ max_age: floored });
  };

  const toggleFilter = (t) => setActiveFilters(f => f.includes(t) ? f.filter(x=>x!==t) : [...f, t]);

  const swipe = async (action) => {
    const current = profiles[0];
    if (!current) return;
    setProfiles((p) => p.slice(1));
    setLastSwiped({ profile: current, action });
    try {
      const { data } = await api.post("/swipe", { target_id: current.id, action });
      if (data.match) setMatch(current.name);
    } catch {
      toast.error("Swipe failed");
      // Roll back the visual state so the profile isn't silently lost.
      setProfiles((p) => [current, ...p]);
      setLastSwiped(null);
    }
  };

  const undoLast = async () => {
    if (!lastSwiped || undoing) return;
    setUndoing(true);
    try {
      await api.post("/swipe/undo");
      // Prepend the profile back so the user sees it again.
      setProfiles((p) => [lastSwiped.profile, ...p]);
      setLastSwiped(null);
      setMatch(null); // if a match modal was open, dismiss it since the match was rolled back
      toast.success("Brought them back");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't undo — the chat may already be active");
    } finally {
      setUndoing(false);
    }
  };

  const dropCurrent = () => setProfiles((p) => p.slice(1));

  const current = profiles[0];
  const next = profiles[1];

  // Verify-photo prompt banner — shows for unverified users only, dismissable, remembered per-user
  const isMeVerified = !!user?.profile?.photo_verified;
  const [showVerifyBanner, setShowVerifyBanner] = useState(() => {
    if (isMeVerified) return false;
    try { return !localStorage.getItem("fm_seen_verify_banner"); } catch { return true; }
  });
  const dismissVerifyBanner = () => {
    setShowVerifyBanner(false);
    try { localStorage.setItem("fm_seen_verify_banner", "1"); } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-footy-bg text-white pb-24 grain">
      <header className="sticky top-0 z-30 bg-footy-bg/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-md mx-auto flex items-center justify-between px-5 py-4">
          <h1 className="font-display text-3xl uppercase tracking-wider">Discover</h1>
          <button onClick={load} className="text-slate-300 hover:text-white text-sm font-semibold" data-testid="refresh-discover">Refresh</button>
        </div>
        <div className="max-w-md mx-auto px-5 pb-3 flex items-center gap-2 overflow-x-auto" data-testid="filter-chips">
            <button onClick={()=>{ setPrefsOpen(v=>!v); dismissFilterTip(); }} data-testid="filter-toggle-icon"
              aria-label="Open match preferences"
              className={`relative flex-none w-8 h-8 rounded-full border transition-colors inline-flex items-center justify-center ${prefsOpen ? "bg-footy-red border-footy-red text-white" : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
              <SlidersHorizontal size={14} />
              {showFilterTip && !prefsOpen && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-footy-red animate-pulse ring-2 ring-footy-bg" aria-hidden="true" />
              )}
            </button>
            {myTypes.map(t => (
              <button key={t} onClick={()=>toggleFilter(t)} data-testid={`filter-${t}`}
                className={`flex-none text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${activeFilters.includes(t)
                  ? "bg-footy-red border-footy-red text-white"
                  : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
                {labelForConnection(t)}
              </button>
            ))}
            {hasPostcode && (
              <button onClick={()=>setPrefsOpen(v=>!v)} data-testid="filter-distance"
                className="flex-none text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/15 bg-white/5 text-slate-300 hover:bg-white/10 inline-flex items-center gap-1">
                <MapPin size={12} /> Within {formatDistanceKm(maxKm)}
              </button>
            )}
            <button onClick={()=>setPrefsOpen(v=>!v)} data-testid="filter-age"
              className="flex-none text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/15 bg-white/5 text-slate-300 hover:bg-white/10 inline-flex items-center gap-1">
              <Users2 size={12} /> Ages {minAge}–{maxAge}
            </button>
            {activeFilters.length > 0 && (
              <button onClick={()=>setActiveFilters([])} className="flex-none text-xs text-slate-400 hover:text-white px-2" data-testid="filter-clear">Clear</button>
            )}
          </div>
        {showFilterTip && !prefsOpen && (
          <div className="max-w-md mx-auto px-5 pb-3" data-testid="filter-tip">
            <div className="text-[11px] text-slate-300 bg-footy-red/10 border border-footy-red/30 rounded-lg px-3 py-2 inline-flex items-center gap-2">
              <SlidersHorizontal size={12} className="text-footy-red flex-none" />
              <span>Tap the icon to change your age & distance filters.</span>
              <button onClick={dismissFilterTip} data-testid="filter-tip-dismiss"
                className="ml-auto text-slate-400 hover:text-white" aria-label="Dismiss tip">
                <X size={12} />
              </button>
            </div>
          </div>
        )}
        {prefsOpen && (
          <div className="max-w-md mx-auto px-5 pb-4 space-y-3" data-testid="prefs-panel">
            {hasPostcode && (
              <div data-testid="distance-slider-panel">
                <div className="flex items-center justify-between text-xs uppercase tracking-widest">
                  <span className="text-slate-400">Max distance</span>
                  <span className="text-footy-red font-bold">{formatDistanceKm(maxKm)}</span>
                </div>
                <input type="range" min={5} max={200} step={5} value={maxKm}
                  onChange={(e)=>onMaxKm(parseInt(e.target.value))}
                  className="w-full mt-1 accent-footy-red" data-testid="distance-slider" />
              </div>
            )}
            <div data-testid="age-slider-panel">
              <div className="flex items-center justify-between text-xs uppercase tracking-widest">
                <span className="text-slate-400">Age range</span>
                <span className="text-footy-red font-bold">{minAge} – {maxAge}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Min age</div>
                  <input type="range" min={18} max={99} step={1} value={minAge}
                    onChange={(e)=>onMinAge(parseInt(e.target.value))}
                    className="w-full accent-footy-red" data-testid="min-age-slider" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Max age</div>
                  <input type="range" min={18} max={99} step={1} value={maxAge}
                    onChange={(e)=>onMaxAge(parseInt(e.target.value))}
                    className="w-full accent-footy-red" data-testid="max-age-slider" />
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      <div className="max-w-md mx-auto px-5 pt-6">
        {/* Fellow-fans-first strip — Footymatch's north star. Same-club fans get the top of the deck. */}
        {myClub && (
          <button
            type="button"
            onClick={() => setSameClub(!sameClubFirst)}
            className={`mb-4 w-full rounded-2xl border transition-colors p-3 flex items-center gap-3 text-left ${sameClubFirst ? "border-yellow-500/50 bg-yellow-500/10 hover:bg-yellow-500/15" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
            data-testid="same-club-toggle"
            aria-pressed={sameClubFirst}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-none ${sameClubFirst ? "bg-yellow-400/25 text-yellow-200" : "bg-white/5 text-slate-400"}`}
              style={sameClubFirst ? { boxShadow: `inset 0 0 0 1px ${clubColor(myClub)}` } : {}}>
              <Users2 size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold leading-tight" data-testid="same-club-toggle-label">
                {sameClubFirst ? `Fellow ${myClub} fans first` : `Showing all clubs`}
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                {sameClubFirst
                  ? "Tap to widen — see fans of every club that matches your filters."
                  : `Tap to prioritise other ${myClub} fans at the top of the deck.`}
              </div>
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full flex-none ${sameClubFirst ? "bg-yellow-400/90 text-yellow-950" : "bg-white/10 text-slate-300"}`}>
              {sameClubFirst ? "ON" : "OFF"}
            </span>
          </button>
        )}
        {/* Fan-type filter row — surfaces armchair-loyal ↔ armchair-loyal etc. */}
        <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-1" data-testid="fan-type-filter-row">
          <span className="text-[10px] uppercase tracking-widest text-slate-400 flex-none">Fan type</span>
          {FAN_TYPES.map(ft => {
            const on = activeFanTypes.includes(ft.id);
            return (
              <button key={ft.id} type="button"
                onClick={() => setActiveFanTypes(prev => prev.includes(ft.id) ? prev.filter(x => x !== ft.id) : [...prev, ft.id])}
                className={`flex-none text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-footy-red border-footy-red text-white" : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}
                data-testid={`fan-type-filter-${ft.id}`}
              >
                {ft.tag}
              </button>
            );
          })}
          {activeFanTypes.length > 0 && (
            <button onClick={() => setActiveFanTypes([])}
              className="flex-none text-[10px] text-slate-400 hover:text-white px-1" data-testid="fan-type-filter-clear">Clear</button>
          )}
        </div>
        {showVerifyBanner && (
          <div className="mb-4 rounded-2xl p-4 border border-emerald-400/40 bg-gradient-to-br from-emerald-500/15 to-emerald-900/10" data-testid="verify-banner">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-400/20 text-emerald-300 flex items-center justify-center flex-none">
                <BadgeCheck size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-emerald-100">Get verified — 3× more matches</div>
                <p className="text-xs text-emerald-200/70 mt-0.5">Real fans, real photos. Takes 20 seconds — one selfie, done.</p>
                <div className="mt-2 flex items-center gap-3">
                  <a href="/profile?verify=1" data-testid="verify-banner-cta"
                    className="text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full bg-emerald-400 text-emerald-950 hover:bg-emerald-300 inline-flex items-center gap-1.5">
                    <BadgeCheck size={13} /> Verify now
                  </a>
                  <button onClick={dismissVerifyBanner} data-testid="verify-banner-dismiss"
                    className="text-xs text-emerald-200/60 hover:text-emerald-100">Not now</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {loading ? (
          <div className="aspect-[3/4] rounded-2xl bg-white/5 animate-pulse" />
        ) : !current ? (
          <div className="footy-card p-8 text-center mt-6">
            <Sparkles className="mx-auto text-footy-red mb-3" />
            <h3 className="font-display text-3xl uppercase">No more supporters near you</h3>
            <p className="text-slate-400 mt-2 text-sm">Check back soon — the terraces are filling up.</p>
            <button onClick={load} className="footy-btn-secondary mt-6" data-testid="empty-refresh">Refresh</button>
          </div>
        ) : (
          <div className="relative h-[70vh] max-h-[640px]">
            {next && <ProfileCard p={next} className="absolute inset-0 scale-95 opacity-60" />}
            <AnimatePresence>
              <motion.div key={current.id} className="absolute inset-0"
                drag="x" dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={(_, info) => {
                  if (info.offset.x > 120) swipe("like");
                  else if (info.offset.x < -120) swipe("pass");
                }}
                initial={{ scale: 0.98 }} animate={{ scale: 1 }}
                exit={{ x: 500, opacity: 0, rotate: 20 }} transition={{ duration: 0.25 }}>
                <ProfileCard p={current} />
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {current && (
          <div className="mt-6 flex items-center justify-center gap-4">
            {lastSwiped ? (
              <button onClick={undoLast} disabled={undoing} data-testid="swipe-undo"
                className="w-12 h-12 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-yellow-300 flex items-center justify-center hover:bg-yellow-500/25 active:scale-90 transition disabled:opacity-40"
                aria-label="Undo last swipe" title={`Bring back ${lastSwiped.profile.name}`}>
                <Undo2 size={20} />
              </button>
            ) : (
              <div className="w-12 h-12" aria-hidden="true" /> /* keeps the row balanced */
            )}
            <button onClick={()=>swipe("pass")} data-testid="swipe-pass"
              className="w-16 h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 active:scale-90 transition">
              <X size={28} className="text-slate-200" />
            </button>
            <button onClick={()=>swipe("like")} data-testid="swipe-like"
              className="w-20 h-20 rounded-full bg-footy-red flex items-center justify-center hover:bg-footy-redHover active:scale-90 transition shadow-[0_10px_30px_rgba(230,57,70,0.5)] animate-pulse-red">
              <Heart size={32} className="text-white" fill="currentColor" />
            </button>
            <SafetyMenu targetId={current.id} targetName={current.name} context="profile" onBlocked={dropCurrent} />
          </div>
        )}
      </div>

      <MatchCelebration open={!!match} name={match} onClose={()=>setMatch(null)}
        onUndo={lastSwiped ? undoLast : null} undoing={undoing} />
      <BottomNav />
    </div>
  );
}

function ProfileCard({ p, className = "" }) {
  const { user } = useAuth();
  const color = clubColor(p.club);
  const conn = (p.connection_types || []).map(labelForConnection);
  const initials = (p.name || "?").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();
  const gallery = (p.photos && p.photos.length ? p.photos : (p.photo_url ? [p.photo_url] : []));
  const [idx, setIdx] = useState(0);
  // Reset gallery position whenever the card switches to a different profile
  useEffect(() => { setIdx(0); }, [p.id]);
  const total = gallery.length;
  const activeUrl = gallery[Math.min(idx, Math.max(total - 1, 0))];
  const cycle = (dir) => {
    if (total <= 1) return;
    setIdx((i) => (i + dir + total) % total);
  };
  const myClub = user?.profile?.club || "";
  const isSameClub = !!myClub && !!p.club && myClub.toLowerCase() === (p.club || "").toLowerCase();
  return (
    <div className={`relative w-full h-full rounded-2xl overflow-hidden footy-card ${p.photo_verified ? "!border-emerald-400/70 shadow-[0_0_0_2px_rgba(52,211,153,0.35),0_10px_36px_rgba(52,211,153,0.15)]" : ""} ${isSameClub ? "ring-2 ring-yellow-400/60" : ""} ${className}`} data-testid="profile-card">
      {activeUrl ? (
        <img src={photoSrc(activeUrl)} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center"
          style={{ background: `linear-gradient(160deg, ${color} 0%, #061022 100%)` }}>
          <span className="font-display text-[120px] text-white/30">{initials}</span>
        </div>
      )}
      {/* Tap zones for cycling through the gallery — left third = prev, right two-thirds = next */}
      {total > 1 && (
        <>
          <button
            type="button"
            onPointerDown={(e)=>{ e.stopPropagation(); cycle(-1); }}
            className="absolute left-0 top-0 bottom-1/3 w-1/3 z-10"
            aria-label="Previous photo"
            data-testid="card-photo-prev"
          />
          <button
            type="button"
            onPointerDown={(e)=>{ e.stopPropagation(); cycle(1); }}
            className="absolute right-0 top-0 bottom-1/3 w-2/3 z-10"
            aria-label="Next photo"
            data-testid="card-photo-next"
          />
          {/* Segmented progress bars at top (Hinge/Bumble style) */}
          <div className="absolute top-2 left-2 right-2 flex gap-1 z-20 pointer-events-none">
            {gallery.map((_, i) => (
              <div key={i} className={`h-0.5 flex-1 rounded-full ${i === idx ? "bg-white" : "bg-white/30"}`} />
            ))}
          </div>
        </>
      )}
      {isSameClub && (
        <div
          className="absolute top-0 inset-x-0 z-30 pointer-events-none"
          data-testid="same-club-ribbon"
        >
          <div className="mx-auto w-fit px-4 py-1.5 rounded-b-lg bg-gradient-to-b from-yellow-400 to-yellow-500 text-yellow-950 text-[10px] font-black uppercase tracking-[0.28em] shadow-lg shadow-yellow-500/30"
            style={{ borderLeft: `3px solid ${color}`, borderRight: `3px solid ${color}` }}>
            Fellow {p.club} fan
          </div>
        </div>
      )}
      <div className={`absolute ${isSameClub ? "top-11" : "top-4"} left-4 right-4 flex items-center gap-2 z-20 pointer-events-none`}>
        <span className="text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full text-white"
              style={{ backgroundColor: color }}>{p.club}</span>
        {typeof p.grounds_count === "number" && p.grounds_count > 0 && (
          <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-yellow-500/20 text-yellow-200 border border-yellow-500/40 inline-flex items-center gap-1" data-testid="card-grounds-count" title={`${p.grounds_count} grounds ticked`}>
            <Trophy size={10} /> {p.grounds_count}
          </span>
        )}
        {p.distance_km !== null && p.distance_km !== undefined && (
          <span className="ml-auto text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-black/50 backdrop-blur border border-white/20 inline-flex items-center gap-1" data-testid="card-distance">
            <MapPin size={10} /> {formatDistanceKm(p.distance_km, { km_decimals: 1, mi_decimals: 1 })}
          </span>
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-footy-bg via-footy-bg/90 to-transparent p-6 pt-24 z-20 pointer-events-none">
        <div className="flex items-end gap-3">
          <h3 className="font-display text-4xl uppercase leading-none tracking-tight">{p.name}</h3>
          <span className="font-display text-3xl text-footy-red leading-none">{p.age}</span>
          {p.photo_verified && (
            <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" data-testid="card-verified">
              <ShieldCheck size={12} /> Verified Supporter
            </span>
          )}
        </div>
        <div className="mt-2 flex items-center gap-1 text-slate-300 text-sm">
          <MapPin size={14} /> {p.location}
        </div>
        <p className="mt-3 text-sm text-slate-200 line-clamp-3">{p.bio}</p>
        {p.prompts && p.prompts.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="card-prompts">
            {p.prompts.slice(0, 2).map((pa, i) => (
              <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-2.5">
                <div className="text-[9px] uppercase tracking-widest text-footy-red font-bold leading-tight">{pa.prompt}</div>
                <div className="text-xs text-slate-200 mt-1 line-clamp-2">{pa.answer}</div>
              </div>
            ))}
          </div>
        )}
        {typeof p.banter_tolerance === "number" && (
          <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-400 inline-flex items-center gap-1.5" data-testid="card-banter">
            <span>Banter tolerance</span>
            <span className="font-bold text-footy-red text-xs normal-case tracking-normal">{p.banter_tolerance}%</span>
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {(p.fan_type || []).slice(0, 3).map(ft => (
            <span key={ft} className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-yellow-500/15 text-yellow-200 border border-yellow-500/30" data-testid={`card-fan-type-${ft}`}>
              {tagForFanType(ft)}
            </span>
          ))}
          {conn.map(c => <span key={c} className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-white/10 border border-white/20">{c}</span>)}
        </div>
      </div>
    </div>
  );
}
